import React, {ReactNode} from 'react';
import {createTheme, SvgIcon, SvgIconProps, ThemeProvider} from '@mui/material';

// Tema MUI pentru „Lot echipe”: aceleasi culori, fonturi si raze ca designul Transfer market 2.0 (vtm-*),
// dar construita din componente MUI. Se aplica si dialogurilor, pentru ca ThemeProvider trece prin portal.
export const VTM = {
    red: '#B8102A',
    redDark: '#8E0B20',
    redTint: '#FDE9EC',
    ink: '#141414',
    ink3: '#3A3A40',
    muted: '#6B6B75',
    faint: '#9A9AA5',
    line: '#E6E6EB',
    input: '#D9D9E0',
    bg: '#F4F4F6',
    green: '#1E8E4A',
    greenDark: '#146B38',
    greenTint: '#E6F4EC',
    gold: '#E0A800',
    amberTint: '#FFF4D6',
    amberInk: '#7A5A00',
};

export const HEAD_FONT = "'Barlow Condensed', sans-serif";

// Titlu in stilul .vtm-k / .vtm-ki
export const headSx = (size: number, italic = false) => ({
    fontFamily: HEAD_FONT, fontWeight: 800, fontSize: size, lineHeight: 1.05, textTransform: 'uppercase' as const,
    letterSpacing: '.02em', fontStyle: italic ? 'italic' : 'normal',
});

// Eticheta mica in stilul .vtm-lbl
export const labelSx = {fontSize: 11, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase' as const, color: VTM.faint};

const lotTheme = createTheme({
    palette: {
        primary: {main: VTM.red, dark: VTM.redDark, contrastText: '#fff'},
        secondary: {main: VTM.ink, contrastText: '#fff'},
        success: {main: VTM.green, dark: VTM.greenDark},
        warning: {main: VTM.gold, contrastText: VTM.ink},
        error: {main: VTM.red},
        text: {primary: VTM.ink, secondary: VTM.muted},
        divider: VTM.line,
        background: {default: '#fff', paper: '#fff'},
    },
    typography: {
        // html-ul aplicatiei (Facit) are font-size 13px; fara asta, tot ce MUI masoara in rem (etichete, helper text) iese prea mic
        htmlFontSize: 13,
        fontFamily: "'Nunito', sans-serif",
        button: {textTransform: 'none', fontWeight: 800},
    },
    shape: {borderRadius: 12},
    components: {
        MuiButton: {
            defaultProps: {disableElevation: true},
            styleOverrides: {
                root: {borderRadius: 12, minHeight: 44, padding: '10px 18px', fontSize: 15},
                sizeSmall: {minHeight: 36, padding: '6px 14px', fontSize: 14},
                outlined: {borderColor: VTM.input, color: VTM.ink, '&:hover': {borderColor: VTM.ink, background: 'transparent'}},
            },
        },
        MuiIconButton: {styleOverrides: {root: {borderRadius: 12}}},
        MuiCard: {
            defaultProps: {variant: 'outlined'},
            styleOverrides: {root: {borderRadius: 18, borderColor: VTM.line}},
        },
        MuiDialog: {styleOverrides: {paper: {borderRadius: 22}, paperFullScreen: {borderRadius: 0}}},
        MuiDialogTitle: {styleOverrides: {root: {padding: '22px 64px 14px 26px'}}},
        MuiDialogContent: {styleOverrides: {root: {padding: '6px 26px 20px'}}},
        MuiDialogActions: {styleOverrides: {root: {padding: '14px 26px 22px', gap: 8, borderTop: `1px solid ${VTM.line}`}}},
        MuiTabs: {styleOverrides: {indicator: {display: 'none'}, flexContainer: {gap: 8}}},
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none', fontWeight: 700, fontSize: 15, minHeight: 44, padding: '10px 18px', borderRadius: 999,
                    border: `1px solid ${VTM.line}`, color: VTM.ink3, opacity: 1,
                    '&:hover': {borderColor: VTM.red, color: VTM.red},
                    '&.Mui-selected': {background: VTM.ink, color: '#fff', borderColor: VTM.ink},
                },
            },
        },
        MuiChip: {styleOverrides: {root: {fontWeight: 800, borderRadius: 8}}},
        MuiOutlinedInput: {styleOverrides: {root: {borderRadius: 12, background: '#fff'}, notchedOutline: {borderColor: VTM.input}}},
        MuiAlert: {styleOverrides: {root: {borderRadius: 14, alignItems: 'center'}, message: {fontWeight: 600, fontSize: 14}}},
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none', fontWeight: 700, fontSize: 15, borderRadius: 12, color: VTM.ink3, borderColor: VTM.input,
                    '&.Mui-selected, &.Mui-selected:hover': {background: VTM.ink, color: '#fff'},
                },
            },
        },
        MuiMenu: {styleOverrides: {paper: {borderRadius: 14, border: `1px solid ${VTM.line}`, boxShadow: '0 12px 32px rgba(20,20,24,.12)'}}},
        MuiMenuItem: {styleOverrides: {root: {fontWeight: 600, fontSize: 14, minHeight: 40}}},
        MuiTooltip: {styleOverrides: {tooltip: {background: VTM.ink, fontSize: 12, fontWeight: 600, borderRadius: 8}}},
        MuiTableCell: {styleOverrides: {head: {fontWeight: 800, color: VTM.muted, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em'}}},
        MuiStepLabel: {styleOverrides: {label: {fontWeight: 700}}},
    },
});

export const LotThemeProvider = ({children}: {children: ReactNode}) => <ThemeProvider theme={lotTheme}>{children}</ThemeProvider>;

// ---------- Iconite (MUI SvgIcon; proiectul nu are @mui/icons-material) ----------

const icon = (path: ReactNode) => (props: SvgIconProps) => (
    <SvgIcon {...props} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2.2}
             strokeLinecap='round' strokeLinejoin='round' sx={{fill: 'none', ...props.sx}}>{path}</SvgIcon>
);

export const CloseIcon = icon(<path d='M6 6l12 12M18 6 6 18'/>);
export const MoreIcon = icon(<><circle cx='12' cy='5' r='1.2'/><circle cx='12' cy='12' r='1.2'/><circle cx='12' cy='19' r='1.2'/></>);
export const SearchIcon = icon(<><circle cx='11' cy='11' r='7'/><path d='m20 20-3.5-3.5'/></>);
export const VerifiedIcon = icon(<><circle cx='12' cy='12' r='9'/><path d='m8 12 3 3 5-6'/></>);
export const BlockedIcon = icon(<><circle cx='12' cy='12' r='9'/><path d='m6 6 12 12'/></>);
export const PhoneIcon = icon(<path d='M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z'/>);
export const PitchIcon = icon(<><rect x='3' y='5' width='18' height='14' rx='2'/><path d='M12 5v14'/><circle cx='12' cy='12' r='2.5'/></>);
export const BadgeIcon = icon(<><path d='M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6z'/><path d='m9 12 2 2 4-4'/></>);
export const TeamIcon = icon(<><circle cx='9' cy='8' r='3.5'/><path d='M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6'/><path d='M16 4.5a3.5 3.5 0 0 1 0 7M18 14c2.2.6 3.5 2.6 3.5 6'/></>);
export const PenIcon = icon(<><path d='M4 20h4L19 9l-4-4L4 16z'/><path d='m13.5 6.5 4 4'/></>);
export const LockIcon = icon(<><rect x='5' y='11' width='14' height='10' rx='2'/><path d='M8 11V8a4 4 0 0 1 8 0v3'/></>);
export const EyeIcon = icon(<><path d='M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z'/><circle cx='12' cy='12' r='3'/></>);
export const EyeOffIcon = icon(<><path d='M3 3l18 18'/><path d='M10.6 5.1A10 10 0 0 1 12 5c6.4 0 10 7 10 7a17 17 0 0 1-3.2 4.1M6.2 6.2A17 17 0 0 0 2 12s3.6 7 10 7a9.7 9.7 0 0 0 4.4-1'/><path d='M9.9 9.9a3 3 0 0 0 4.2 4.2'/></>);
export const CheckIcon = icon(<path d='m5 12 5 5L20 7'/>);
export const InfoIcon = icon(<><circle cx='12' cy='12' r='9'/><path d='M12 11v5M12 8h.01'/></>);
export const ListIcon = icon(<><path d='M9 6h11M9 12h11M9 18h11'/><path d='M4 6h.01M4 12h.01M4 18h.01'/></>);
export const BellIcon = icon(<><path d='M6 8a6 6 0 0 1 12 0c0 7 3 8 3 8H3s3-1 3-8'/><path d='M10.3 20a2 2 0 0 0 3.4 0'/></>);
export const ToggleIcon = icon(<><rect x='2' y='7' width='20' height='10' rx='5'/><circle cx='16' cy='12' r='2.5'/></>);
export const UserIcon = icon(<><circle cx='12' cy='8' r='4'/><path d='M4 21c0-4 3.6-7 8-7s8 3 8 7'/></>);
export const RefreshIcon = icon(<><path d='M20 11a8 8 0 1 0-2.3 5.7'/><path d='M20 4v7h-7'/></>);

export default lotTheme;
export const WarningIcon = icon(<><path d='M12 3 2 20h20z'/><path d='M12 10v4M12 17h.01'/></>);
export const ErrorIcon = icon(<><circle cx='12' cy='12' r='9'/><path d='m9 9 6 6M15 9l-6 6'/></>);
